/**
 * HiBro 客户端 SDK 接入。仅在确认有宿主时才动态加载 `@hibro/sdk`，
 * 避免普通浏览器 / 单测在启动时拉起 SDK。
 */

type IframeSdk = {
  executeCommand: (command: string, payload?: unknown) => Promise<unknown>
  onEvent: (event: string, handler: (data: unknown) => void) => () => void
}

function inIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent !== window
  } catch {
    return true
  }
}

function hasPreloadBridge(): boolean {
  return typeof globalThis !== 'undefined' && '__hibroWebviewBridge' in globalThis
}

function resolveDebugViewId(): string | undefined {
  if (typeof window !== 'undefined') {
    const fromQuery = new URLSearchParams(window.location.search).get('hibroDebugView')
    if (fromQuery) return fromQuery
  }
  const fromEnv = import.meta.env.VITE_HIBRO_DEBUG_VIEW
  return fromEnv || undefined
}

/** 同步判断：iframe / webview preload / 调试反连。不创建 SDK。 */
export function isHostAvailable(): boolean {
  if (typeof window === 'undefined') return false
  if (inIframe() || hasPreloadBridge()) return true
  return Boolean(resolveDebugViewId())
}

function buildDebugServerUrl(viewId: string): string {
  const host = import.meta.env.VITE_HIBRO_DEBUG_HOST ?? '127.0.0.1'
  const port = import.meta.env.VITE_HIBRO_DEBUG_PORT ?? '9230'
  const token = import.meta.env.VITE_HIBRO_DEBUG_TOKEN ?? 'hibro-debug'
  const params = new URLSearchParams({
    kind: 'iframe',
    id: viewId,
    token,
  })
  return `ws://${host}:${port}?${params.toString()}`
}

let sdkPromise: Promise<IframeSdk | null> | null = null

export function getSdk(): Promise<IframeSdk | null> {
  if (!sdkPromise) sdkPromise = loadSdk()
  return sdkPromise
}

async function loadSdk(): Promise<IframeSdk | null> {
  if (!isHostAvailable()) return null

  try {
    const { createIframeSdk } = await import('@hibro/sdk/iframe')
    if (inIframe() || hasPreloadBridge()) {
      return createIframeSdk() as IframeSdk
    }
    const viewId = resolveDebugViewId()
    if (viewId) {
      return createIframeSdk({
        debugServer: { viewId, url: buildDebugServerUrl(viewId) },
      }) as IframeSdk
    }
  } catch (error) {
    console.warn('[devSdk] createIframeSdk failed:', error)
  }
  return null
}
